import { Link } from '@mui/material';
import { t } from 'i18next';
import { PATHS } from '../../../routing/routes';
import { BlankCell } from '../../components/Cells/BlankCell';
import { FilterConfiguration } from '../../components/FilterPanel/types';
import { TypographyOverflow } from '../../components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../controllers/PaginatedTableController';
import { TraceType } from '../../enums/TraceType';
import { getOperationContextLabel } from '../../operations/enums/OperationContext';
import { getOperationTypeLabel } from '../../operations/enums/OperationType';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../types/pagination';
import { UrlHelper } from '../../utils/UrlHelper';
import { KeyOperation } from '../models/KeyOperation';
import { KeyOperationsService } from '../services/KeyOperationsService';

type State = PaginatedTableState & {};

type Props = {
  keyId: number;
  traceType: TraceType | null;
  initialData?: PaginationResult<KeyOperation> | null;
  parentLoading?: boolean;
  withPrefill?: boolean;
};

export const DEFAULT_KEY_OPERATIONS_PAGE_SIZE = 10;

export class KeyOperationsTable extends PaginatedTableController<KeyOperation, Props, State> {
  private readonly operationsService: KeyOperationsService;

  static defaultProps = {
    initialData: null,
    parentLoading: false,
    withPrefill: false,
  };

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.operationsService = new KeyOperationsService();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.operationsService.cancelAllRequests();
  }

  protected getPrefilledConfig() {
    if (!this.props.withPrefill) {
      return super.getPrefilledConfig();
    }

    return {
      prefillActive: true,
      initialLoading: this.props.parentLoading,
      initialData: this.props.initialData,
    } as PrefilledDataConfig<KeyOperation>;
  }

  protected getDefaultPageSize(): number {
    return DEFAULT_KEY_OPERATIONS_PAGE_SIZE;
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('operations') };
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected getFieldConfigurations(): FieldConfiguration<KeyOperation>[] {
    return [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: {
          maxWidth: 100,
        },
        render: (model, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.REPORT_OPERATION, { reportId: model.getReportId().toString(), operationId: model.getId().toString() })}
            aria-label={t('a11y.report.keys.operationLabel', { operationId: model.getId(), keyId: this.props.keyId }) as string}
          >
            {model.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'type',
        label: t('type'),
        description: t('type'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getOperationTypeLabel(model.getType())}</TypographyOverflow>,
      },
      {
        fieldKey: 'context',
        label: t('context'),
        description: t('context'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getOperationContextLabel(model.getContext())}</TypographyOverflow>,
      },
      {
        fieldKey: 'algorithm',
        label: t('algorithms'),
        description: t('algorithms'),
        render: (model, { hasFocus }) => {
          const algorithms = model.getAlgorithms();
          if (algorithms.length > 0) {
            return <TypographyOverflow hasFocus={hasFocus}>{algorithms.join(', ')}</TypographyOverflow>;
          }
          return <BlankCell blankText={t('none')} />;
        },
      },
      {
        fieldKey: 'keyRole',
        label: t('keyRole'),
        description: t('keyRole'),
        render: (model, { hasFocus }) => {
          const roles = model.getKeyRoles();
          if (roles.length > 0) {
            return <TypographyOverflow hasFocus={hasFocus}>{roles.join(', ')}</TypographyOverflow>;
          }
          return <BlankCell blankText={t('none')} />;
        },
      },
    ];
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<KeyOperation>> {
    try {
      return await this.operationsService.getKeyOperations(this.props.keyId, cursorInfo, this.getFilters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }
}
